export const APP_CONFIG = {
  tacSystemOfRecordValue: 'TAC',
  isSidemenuOpenByDefault: true,
  snackbarDurationMs: 5000,
  fullscreenDialogCss: {
    width: '95vw',
    height: '90vh',
  },
  midsizeDialogCss: {
    width: '34rem',
    maxWidth: '34rem',
    maxHeight: '95vh',
    position: { top: '6rem' },
  },
};

export const ERROR_MESSAGES = {
  default: {
    title: "We're currently experiencing technical difficulties.",
    message: 'Please try again later, or contact your IT support team.',
  },
  unauthorized: {
    title: 'Unauthorized',
    message: 'You are not authorized to access this component. Please contact your IT support team for more details.',
  },
};

// use these for the filtering dropdown in Trailer Release
export const EQUIPMENT_FILTERS = [
  {
    value: 'all',
    label: 'All',
  },
  {
    value: 'at-consignee',
    label: 'At Consignee',
  },
  {
    value: 'available-for-delivery',
    label: 'Available for Delivery',
  },
  {
    value: 'northbound',
    label: 'Northbound',
  },
  {
    value: 'southbound',
    label: 'Southbound',
  },
];

// use these to determine which actions are available per equipment
export const EQUIPMENT_STATUS_MAPPINGS = [
  {
    code: 'AVAILABLE', // Available
    availableActions: ['deliver'],
  },
  {
    code: 'IN-TRANSIT', // Northbound
    availableActions: [],
  },
  {
    code: 'SPOTTED', // AT HD
    availableActions: ['release'],
  },
  {
    code: 'RELEASED',
    availableActions: [],
  },
];

// list of scopes that are sent by b/e
export const APP_SCOPES = {
  customer: 'ymt:customer',
  terminalUser: 'ymt:user',
  activityManager: 'ymt:activitymanager',
  adHocTrailerManager: 'ymt:adhoctrailermanager',
  admin: 'ymt:admin',
  shunter: 'ymt:shunter',
};

// access a specific page's info via APP_PAGES.<page_name> or as an array by running Object.values(APP_PAGES)
export const APP_PAGES = {
  home: {
    requiredScopes: [],
    title: 'Dashboard',
    description: 'View alerts, pending tasks, and available pages.',
    isVisibleIn: {
      sidemenu: true,
      dashboard: false,
    },
    url: 'dashboard',
    matIconName: 'dashboard',
  },
  inbound: {
    requiredScopes: [APP_SCOPES.terminalUser],
    title: 'Inbound',
    description: 'View incoming equipment and assign parking zones.',
    isVisibleIn: {
      sidemenu: true,
      dashboard: true,
    },
    url: 'inbound',
    matIconName: 'local_shipping',
  },
  shop: {
    requiredScopes: [APP_SCOPES.terminalUser],
    title: 'Shop',
    description: "View equipment located at this facility's shop.",
    isVisibleIn: {
      sidemenu: true,
      dashboard: true,
    },
    url: 'shop',
    matIconName: 'handyman',
  },
  doors: {
    requiredScopes: [APP_SCOPES.terminalUser],
    title: 'Doors',
    description: "View all equipment located at this facility's doors.",
    isVisibleIn: {
      sidemenu: true,
      dashboard: true,
    },
    url: 'doors',
    matIconName: 'door_back',
  },
  yard: {
    requiredScopes: [APP_SCOPES.terminalUser],
    title: 'Yard',
    description: 'View equipment currently located at the yard.',
    isVisibleIn: {
      sidemenu: true,
      dashboard: true,
    },
    url: 'yard',
    matIconName: 'warehouse',
  },
  activities: {
    title: 'Activities',
    url: 'activities',
    description: 'Queue work requests for shunters and equipment.',
    isVisibleIn: {
      sidemenu: true,
      dashboard: true,
    },
    matIconName: 'task',
    requiredScopes: [APP_SCOPES.terminalUser],
  },
  equipment: {
    requiredScopes: [APP_SCOPES.terminalUser],
    title: 'Equipment',
    url: 'equipment',
    description: 'Search and view all equipment.',
    isVisibleIn: {
      sidemenu: true,
      dashboard: true,
    },
    matIconName: 'list_alt',
    equipmentRoutes: {
      all: {
        title: 'Equipment',
        url: 'all',
      },
      inbound: {
        title: 'Inbound',
        url: 'inbound',
      },
      yard: {
        title: 'Yard',
        url: 'yard',
      },
    },
  },
  trailerRelease: {
    title: 'Trailer Release',
    url: 'trailer-release',
    description: 'Perform status updates on equipment such as initiating deliveries and releasing trailers.',
    isVisibleIn: {
      sidemenu: true,
      dashboard: true,
    },
    matIconName: 'local_shipping',
    requiredScopes: [APP_SCOPES.customer],
  },
  reports: {
    title: 'Reports',
    url: 'reports',
    description: 'Generate reports on shunter activities.',
    isVisibleIn: {
      sidemenu: true,
      dashboard: true,
    }, // TODO: re-enable for relevant release
    matIconName: 'analytics',
    requiredScopes: [APP_SCOPES.terminalUser],
  },
  admin: {
    title: 'Admin',
    url: 'admin',
    description: 'Manage operational resources and account settings.',
    isVisibleIn: {
      sidemenu: true,
      dashboard: true,
    },
    matIconName: 'settings',
    requiredScopes: [APP_SCOPES.terminalUser],
    adminRoutes: {
      facilitySettings: {
        title: 'Facility Settings',
        url: 'facility-settings',
        description: 'Change settings related to company facilities and customer yards.',
        matIconName: 'warehouse',
        isVisibleIn: { admin: true },
      },
      yardReeferCheckSettings: {
        title: 'Yard/Reefer Check Settings',
        url: 'yard-reefer-settings',
        description: "Change your location's yard/reefer check settings.",
        matIconName: 'checklist',
        isVisibleIn: { admin: false },
      },
      shunterSettings: {
        title: 'Shunter Settings',
        url: 'shunter-settings',
        description: "Change your location's shunter settings.",
        matIconName: 'swap_horizontal_circle',
        isVisibleIn: { admin: false },
      },
      userPermissions: {
        title: 'User Permissions',
        url: 'user-permissions',
        description: 'Add, modify, and delete user permission settings.',
        matIconName: 'admin_panel_settings',
        isVisibleIn: { admin: true },
      },
      alertsConfig: {
        title: 'Alerts Configuration',
        url: 'alerts-config',
        description: 'Modify maximum temperature variance and service level settings.',
        matIconName: 'thermostat',
        isVisibleIn: { admin: true },
      },
    },
    userRoutes: {
      addEquipment: {
        title: 'Ad Hoc Equipment',
        url: 'adhoc',
        description: 'Create, edit, and delete TAC-managed equipment.',
        matIconName: 'add_box',
        requiredScopes: [APP_SCOPES.adHocTrailerManager],
        isVisibleIn: { admin: true },
      },
      archivedEquipment: {
        title: 'Archived Equipment',
        url: 'archived',
        description: 'View and reactivate archived equipment.',
        matIconName: 'sync_disabled',
        requiredScopes: [APP_SCOPES.adHocTrailerManager],
        isVisibleIn: { admin: true },
      },
      accountManagement: {
        title: 'My Account',
        url: 'my-account',
        description: 'Change your username, password, and other user preferences.',
        matIconName: 'manage_accounts',
        requiredScopes: [],
        isVisibleIn: { admin: true },
      },
      support: {
        title: 'Support',
        url: 'support',
        description: 'Submit a bug report or contact your IT team.',
        matIconName: 'contact_support',
        requiredScopes: [],
        isVisibleIn: { admin: false },
      },
    },
  },
  login: {
    title: 'Login',
    url: 'login',
  },
  unauthorized: {
    title: 'Unuauthorized',
    url: 'unauthorized',
  },
};

// utility const to lookup a page obj (or its existence) using the page's url
export const APP_PAGES_DICT: Record<string, any> = Object.values(APP_PAGES).reduce(
  (dict, page) => {
    dict[page.url] = page;
    return dict;
  },
  {} as Record<string, any>
);

export const APP_EQUIPMENT_TABLE_COLUMNS = [
  {
    columnName: 'hasAlerts', // HasAlerts
    isVisibleIn: {
      doorsAndShops: false,
      inbound: false,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'equipmentId', // EquipmentId
    isVisibleIn: {
      doorsAndShops: true,
      inbound: true,
      yard: true,
      allEquipment: true,
    },
  },
    {
    columnName: 'p1Expiration', // HasAlerts
    isVisibleIn: {
      doorsAndShops: true,
      inbound: false,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'tripNumber', // Dispatch.Planning.CurrentTrip
    isVisibleIn: {
      doorsAndShops: true,
      inbound: true,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'createdBySystem', // Dispatch.Planning.CreatedBySystem
    isVisibleIn: {
      doorsAndShops: false,
      inbound: true,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'typeCode', // Dispatch.Planning.TrailerTypeExternalCode
    isVisibleIn: {
      doorsAndShops: true,
      inbound: true,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'tacStatus', // Equipment.Status
    isVisibleIn: {
      doorsAndShops: true,
      inbound: true,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'driver1', // Dispatch.Planning.DriverId1
    isVisibleIn: {
      doorsAndShops: false,
      inbound: true,
      yard: false,
      allEquipment: false,
    },
  },
  {
    columnName: 'tractor', // Dispatch.Planning.VehicleId1
    isVisibleIn: {
      doorsAndShops: false,
      inbound: true,
      yard: false,
      allEquipment: false,
    },
  },
  {
    columnName: 'carrier', // // Dispatch.Planning.CarrierId1
    isVisibleIn: {
      doorsAndShops: false,
      inbound: true,
      yard: false,
      allEquipment: false,
    },
  },
  {
    columnName: 'stopEta', // // Dispatch.Planning.CurrentStopEstimatedArrivalTime
    isVisibleIn: {
      doorsAndShops: false,
      inbound: true,
      yard: false,
      allEquipment: false,
    },
  },
  {
    columnName: 'legEndCompany', // // Dispatch.Planning.EndFacilityId
    isVisibleIn: {
      doorsAndShops: false,
      inbound: true,
      yard: false,
      allEquipment: false,
    },
  },
  {
    columnName: 'legOutStatus', // Dispatch.Planning.Status
    isVisibleIn: {
      doorsAndShops: false,
      inbound: true,
      yard: false,
      allEquipment: false,
    },
  },
  {
    columnName: 'terminalZone',
    isVisibleIn: {
      doorsAndShops: false,
      inbound: false,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'location',
    isVisibleIn: {
      doorsAndShops: false,
      inbound: false,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'setpoint',
    isVisibleIn: {
      doorsAndShops: true,
      inbound: false,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'actualTemp',
    isVisibleIn: {
      doorsAndShops: true,
      inbound: false,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'fuelLevel',
    isVisibleIn: {
      doorsAndShops: true,
      inbound: false,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'notes', // Notes
    isVisibleIn: {
      doorsAndShops: true,
      inbound: true,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'plannedParkingZone',
    isVisibleIn: {
      doorsAndShops: false,
      inbound: true,
      yard: false,
      allEquipment: false,
    },
  },
  {
    columnName: 'pendingActivityDetails',
    isVisibleIn: {
      doorsAndShops: true,
      inbound: false,
      yard: false,
      allEquipment: false,
    },
  },
  {
    columnName: 'pendingActivityShunterId',
    isVisibleIn: {
      doorsAndShops: true,
      inbound: false,
      yard: false,
      allEquipment: false,
    },
  },
  {
    columnName: 'lastObserved',
    isVisibleIn: {
      doorsAndShops: false,
      inbound: false,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'sourceNames',
    isVisibleIn: {
      doorsAndShops: false,
      inbound: false,
      yard: true,
      allEquipment: true,
    },
  },
  {
    columnName: 'actions',
    isVisibleIn: {
      doorsAndShops: true,
      inbound: true,
      yard: true,
      allEquipment: true,
    },
  },
];

export const EQUIPMENT_ADDITIONAL_ACTIONS = [
  {
    value: 'fuelCheck',
    label: 'Fuel Check',
    icon: 'local_gas_station',
  },
  {
    value: 'fuelPump',
    label: 'Fuel Pump',
    icon: 'local_gas_station',
  },
  {
    value: 'weightCheck',
    label: 'Weight Check',
    icon: 'scale',
  },
  {
    value: 'reeferPreTrip',
    label: 'Reefer Pre-Trip',
    icon: 'ac_unit',
  },
  {
    value: 'wash',
    label: 'Wash',
    icon: 'shower',
  },
  {
    value: 'other',
    label: 'Other',
    icon: 'help_outline',
  },
  {
    value: 'temperatureCheck',
    label: 'Temperature Check',
    icon: 'thermostat',
  },
];
